import { useState, useEffect } from 'react';
import { FileText, Calendar, Search, BookOpen, Clock } from 'lucide-react';
import api from '../../services/api';

export default function StudentExams() {
  const [upcomingExams, setUpcomingExams] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => { loadExams(); }, []);

  const loadExams = async () => {
    try {
      const [upcomingRes, resultsRes] = await Promise.all([
        api.get('/student/upcoming-exams'),
        api.get('/student/exam-results')
      ]);
      if (upcomingRes.data.success && upcomingRes.data.data) {
        setUpcomingExams(upcomingRes.data.data.exams || []);
      }
      if (resultsRes.data.success && resultsRes.data.data) {
        setResults(resultsRes.data.data.results || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const examsToDisplay = activeTab === 'upcoming' ? upcomingExams : results;
  
  // Further filter upcoming exams by date (in case backend returns completed ones)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const actualExamsToDisplay = activeTab === 'upcoming'
    ? examsToDisplay.filter(e => {
        const examDate = new Date(e.exam_date);
        examDate.setHours(0, 0, 0, 0);
        // Only show if date is in future AND no marks entered yet
        return examDate >= today;
      })
    : examsToDisplay;
  
  const filteredExams = actualExamsToDisplay.filter(e =>
    e.exam_name.toLowerCase().includes(search.toLowerCase()) ||
    e.subject_name.toLowerCase().includes(search.toLowerCase())
  );

  // Group exams by subject
  const examsBySubject = filteredExams.reduce((acc, exam) => {
    if (!acc[exam.subject_name]) {
      acc[exam.subject_name] = [];
    }
    acc[exam.subject_name].push(exam);
    return acc;
  }, {});

  const getStatusColor = (date) => {
    const examDate = new Date(date);
    const now = new Date();
    if (examDate < now) return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Completed' };
    return { bg: 'bg-green-100', text: 'text-green-600', label: 'Upcoming' };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Exams</h1>
        <p className="text-sm sm:text-base text-gray-600">View your upcoming exams and past results</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 sm:gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`px-3 sm:px-4 py-2 text-sm sm:text-base font-medium border-b-2 transition ${
            activeTab === 'upcoming'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Upcoming Exams ({upcomingExams.length})
        </button>
        <button
          onClick={() => setActiveTab('results')}
          className={`px-3 sm:px-4 py-2 text-sm sm:text-base font-medium border-b-2 transition ${
            activeTab === 'results'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-600 hover:text-gray-800'
          }`}
        >
          Results ({results.length})
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input type="text" placeholder="Search exams or subjects..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

      {/* Exams by Subject */}
      {Object.keys(examsBySubject).length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-12 text-center">
          <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-600">No {activeTab} exams found</h3>
          <p className="text-sm sm:text-base text-gray-400 mt-2">Your {activeTab === 'upcoming' ? 'upcoming exam schedule' : 'exam results'} will appear here</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(examsBySubject).map(([subject, subjectExams]) => (
            <div key={subject} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-3 sm:px-5 py-3 sm:py-4 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-indigo-100 rounded-lg">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800 truncate">{subject}</h3>
                    <p className="text-xs sm:text-sm text-gray-500">{subjectExams.length} exam{subjectExams.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {subjectExams.map((exam) => {
                  const status = getStatusColor(exam.exam_date);
                  // Fix percentage calculation for Results tab
                  const examTotalMarks = activeTab === 'results' ? exam.exam_total_marks : exam.total_marks;
                  const obtainedMarks = activeTab === 'results' ? exam.total_marks : exam.obtained_marks;
                  const percentage = examTotalMarks > 0 && obtainedMarks !== undefined ? Math.round((obtainedMarks / examTotalMarks) * 100) : 0;
                  return (
                    <div key={exam.exam_id} className="p-4 sm:p-5 hover:bg-gray-50 transition">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        {/* Left side - Exam details */}
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                            <FileText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2">{exam.exam_name}</h4>
                            
                            {/* Date and Grade */}
                            <div className="flex flex-col gap-1.5 mb-2">
                              <span className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>{exam.exam_date ? new Date(exam.exam_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'Date TBD'}</span>
                              </span>
                              <span className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600">
                                <BookOpen className="w-4 h-4 text-gray-400" />
                                <span>{exam.grade_name || 'Grade'}</span>
                              </span>
                            </div>

                            {/* Description */}
                            {exam.description && (
                              <p className="text-xs sm:text-sm text-gray-500 mb-2 line-clamp-2">{exam.description}</p>
                            )}

                            {/* Component breakdown for results */}
                            {activeTab === 'results' && exam.components && exam.components.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {exam.components.map((comp, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">
                                    {comp.component_name}: {comp.marks_obtained}/{comp.max_marks}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right side - Status and Score */}
                        <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2">
                          {/* Total Marks Badge */}
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-lg">
                            <Clock className="w-3.5 h-3.5 text-gray-500" />
                            <span className="text-xs font-medium text-gray-700">{examTotalMarks} marks</span>
                          </div>

                          {/* Status Badge */}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                            {status.label}
                          </span>

                          {/* Score Display for Results */}
                          {activeTab === 'results' && obtainedMarks !== undefined && (
                            <div className="flex flex-col items-end gap-1 mt-1">
                              <div className="flex items-baseline gap-1">
                                <span className="text-base sm:text-lg font-bold text-gray-800">{obtainedMarks}</span>
                                <span className="text-xs text-gray-500">/ {examTotalMarks}</span>
                              </div>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${percentage >= 75 ? 'bg-green-100 text-green-700' : percentage >= 60 ? 'bg-blue-100 text-blue-700' : percentage >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                {percentage}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
